/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

export interface TpConfigInfo {
  label: string;
  icon: string;
  description: string;
  route: any[];
  fragment: string;
}

export interface TpConfigsInfo {
  [key: string]: TpConfigInfo;
}

export interface TpSections {
  [key: string]: {
    label: string;
    icon: string;
    color: string;
    description: string;
    items: TpConfigsInfo;
  };
}

export const TP_BASE_SECTIONS_INFO: TpConfigsInfo = {
  // Base treatment plan
  icd9: {
    label: "Diagnosi secondo ICD-9",
    icon: "notebook-heart",
    description:
      "Codifica della diagnosi secondo la Classificazione Internazionale delle Malattie, 9ª Revisione (ICD-9).",
    route: [{outlets: {"treatment-plan": "base"}}],
    fragment: "anchor-icd9",
  },
  operations: {
    label: "Operazioni secondo ICD-9",
    icon: "iv-bag",
    description: "Aggiunta e modifica data operazioni da effettuare.",
    route: [{outlets: {"treatment-plan": "base"}}],
    fragment: "anchor-operations",
  },
  therapies: {
    label: "Terapie e Farmaci",
    icon: "pill",
    description: "Terapia farmaceutica del paziente.",
    route: [{outlets: {"treatment-plan": "base"}}],
    fragment: "anchor-therapies",
  },

  exams: {
    label: "Esami",
    icon: "file-chart",
    description: "Esami ambulatoriali e di laboratorio.",
    route: [{outlets: {"treatment-plan": "base"}}],
    fragment: "anchor-exams",
  },
  tasks: {
    label: "Task",
    icon: "calendar-multiple",
    description: "Attività che il paziente deve svolgere.",
    route: [{outlets: {"treatment-plan": "base"}}],
    fragment: "anchor-task",
  },
  // diet: {
  //   label: "Dieta",
  //   icon: "food-apple",
  //   description: `Indicazioni sull'alimentazione del paziente.`,
  //   route: [{ outlets: { "treatment-plan": "base" } }],
  //   fragment: "anchor-diet",
  // },
  // "physical-activity": {
  //   label: "Attività Fisica",
  //   icon: "run",
  //   description: `Indicazioni sull'attività fisica che il paziente deve svolgere.`,
  //   route: [{ outlets: { "treatment-plan": "base" } }],
  //   fragment: "anchor-physical-activity",
  // },
};

export const TP_ADV_SECTIONS_INFO: TpConfigsInfo = {
  // Adv treatment plan
  "app-features": {
    label: "Inserimento Dati App",
    icon: "cellphone-cog",
    description: `Configura quali dati il paziente avrà la possibilità di inserire direttamente dall'applicazione.`,
    route: [{outlets: {"treatment-plan": "advanced"}}],
    fragment: "anchor-app-features",
  },
  anamnesi: {
    label: "Diario anamnesi",
    icon: "note",
    description: `Diario di anamnesi`,
    route: [{outlets: {"treatment-plan": "advanced"}}],
    fragment: "anchor-anamnesi",
  },

  // surveys: {
  //   label: "Questionari",
  //   icon: "format-list-checks",
  //   description: `Questionari`,
  //   route: [{ outlets: { "treatment-plan": "advanced" } }],
  //   fragment: "anchor-surveys",
  // },
  // "personal-experiments": {
  //   label: "Esperimenti Personali",
  //   icon: "flask-outline",
  //   description: `Esperimenti personali`,
  //   route: [{ outlets: { "treatment-plan": "advanced" } }],
  //   fragment: "anchor-personal-experiments",
  // },
  //goals: {
  //label: "Goal",
  //icon: "soccer",
  //description: `Obbiettivi che il paziente dovrà raggiungere`,
  //route: [{ outlets: { "treatment-plan": "advanced" } }],
  //fragment: "anchor-goals",
  //},
  // "education-ondemand": {
  //   label: "Educazione On-Demand",
  //   icon: "chat-processing",
  //   description: `Abilita/Disabilita i moduli On-Demand ai quali il paziente potrà accedere`,
  //   route: [{ outlets: { "treatment-plan": "advanced" } }],
  //   fragment: "anchor-edu-ondemand",
  // },
  // "education-contextual": {
  //   label: "Segnalazioni al paziente",
  //   icon: "comment-alert-outline",
  //   description: `Segnalazioni al paziente`,
  //   route: [{outlets: {"treatment-plan": "advanced"}}],
  //   fragment: "anchor-warnings-patient",
  // },
  microlearning: {
    label: "Educazione Microlearning",
    icon: "comment-question-outline",
    description: `Unità educative`,
    route: [{outlets: {"treatment-plan": "advanced"}}],
    fragment: "anchor-microlearning",
  },
  // "predictive-models": {
  //   label: "Modelli Predittivi",
  //   icon: "finance",
  //   description: "Modelli predittivi",
  //   route: [{ outlets: { "treatment-plan": "advanced" } }],
  //   fragment: "anchor-predictive-models",
  // },
  questionnaires: {
    label: "Questionari",
    icon: "comment-question-outline",
    description: `Questionari assegnati al paziente`,
    route: [{outlets: {"treatment-plan": "advanced"}}],
    fragment: "anchor-questionnaires",
  },
};

if (
  localStorage.getItem("configuratorePdta") &&
  localStorage.getItem("configuratorePdta") === "true"
) {
  TP_ADV_SECTIONS_INFO["pdta"] = {
    label: "Esami prescritti",
    icon: "format-list-checks",
    description: `Lista degli esami assegnati al paziente`,
    route: [{outlets: {"treatment-plan": "advanced"}}],
    fragment: "anchor-pdta",
  };
}

export const TP_MON_SECTIONS_INFO: TpConfigsInfo = {
  // Monitoring treatment plan
  telemonitoring: {
    label: "Telemonitoraggio",
    icon: "binoculars",
    description: `Configura il telemonitoraggio, abilita o disabilita le segnalazioni agli operatori sanitari e la chat con il paziente.`,
    route: [{outlets: {"treatment-plan": "monitoring"}}],
    fragment: "anchor-telemonitoring",
  },
  // 'warnings-doctor': {
  //   label: 'Configurazione Report Straordinari', icon: 'alert', description: 'Configurazione dei report straordinari',
  //   route: [{outlets: {'treatment-plan': 'monitoring'}}],
  //   fragment: 'anchor-warnings-doctor'
  // },
  reports: {
    label: "Configurazione Report Ordinari",
    icon: "file-chart",
    description: "Configurazione dei report ordinari",
    route: [{outlets: {"treatment-plan": "monitoring"}}],
    fragment: "anchor-reports",
  },
};

export const TP_SECTIONS: TpSections = {
  base: {
    label: "Piano di Cura Base",
    icon: "medical-bag",
    color: "#f44336",
    description: "Piano di cura di base per il paziente",
    items: TP_BASE_SECTIONS_INFO,
  },
  advanced: {
    label: "Piano di Cura Avanzato",
    icon: "mortar-pestle-plus",
    color: "#d32f2f",
    description: `Indicazioni per il piano di cura avanzato che prevede l'utilizzo dell'app`,
    items: TP_ADV_SECTIONS_INFO,
  },
  monitoring: {
    label: "Monitoraggio",
    icon: "binoculars",
    color: "#607d8b",
    description: "Impostazioni relative al monitoraggio del paziente",
    items: TP_MON_SECTIONS_INFO,
  },
};

export const TP_ALL_SECTIONS: TpConfigsInfo = Object.values(TP_SECTIONS).reduce((a, b) => {
  Object.entries(b.items).forEach((e) => {
    a[e[0]] = e[1];
  });
  return a;
}, {});
